import '@copilotkit/react-core/v2/styles.css';
import '../styles/chat.css';
import {
  CopilotKitProvider, useAgent, useAgentContext, useConfigureSuggestions, useCopilotKit, useFrontendTool, useHumanInTheLoop,
} from '@copilotkit/react-core/v2';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { api, type BrandFacts, type BrandMetrics } from '../api';
import { errorMessage } from '../components/ui';
import { PhotoChat } from '../components/ChatPhotos';
import { t } from '../i18n';
import { fmtDateTime } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * The owner's chat: what an owner uses most days instead of the studio. The model (api/src/routes/copilot.ts) only talks and shows cards;
 * every tool below runs here, in the owner's browser, as the owner, through the same API the studio uses. Approving is a tap on a card.
 */
interface Waiting {
  timezone: string;
  checklist: string[];
  posts: { pieceId: string; versionId: string; title: string; slotAt: string | null; text: string; network: string | null; account: string | null }[];
}
interface FactsCardData { url: string | null; facts: { id: string; text: string }[] }
interface PlanCardData { month: string; planId: string; state: string; summary: string; items: { day: string; theme: string; content_type: string }[]; questions: { id: string; question: string }[] }

const parse = <T,>(r: unknown): T | null => {
  if (r == null || r === '') return null;
  if (typeof r !== 'string') return r as T;
  try { return JSON.parse(r) as T; } catch { return null; }
};
/** In every tool result: the owner already sees the card, so the model should not say it again. */
const SHOWN = 'note_for_assistant: the owner already sees all of this on a card. Do not repeat it; reply with one short sentence at most';
/** The business's website, as the owner knows it: the address the facts are read from, without its path. */
const site = (url: string) => { try { return new URL(url).origin; } catch { return url; } };
const monthLabel = (m: string) => DateTime.fromISO(`${m}-01`).setLocale('en-GB').toFormat('LLLL yyyy');

function Card({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return <div className="ch-card">{title && <div className="ch-card-title">{title}</div>}{children}</div>;
}

/** What became of a version a card showed (GET …/chat/shown). Only 'waiting' can still be approved from that card. */
type ShownState = 'approved' | 'waiting' | 'replaced' | 'changes_requested' | 'gone';

/**
 * The week's posts, with one Approve all. The card may come back with the conversation (a reload, another device), long after it was
 * shown: it asks what became of exactly the versions it shows and says so, and its button approves only those still waiting. It never
 * approves a newer version it did not show, and tapping it again after a reload cannot repeat what was done.
 */
function WeekCard({ data, brandId }: { data: Waiting; brandId: string }) {
  const [shown, setShown] = useState<Record<string, ShownState> | null>(null);
  const [handoff, setHandoff] = useState<Record<string, 'manual' | 'automatic' | null>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const ids = data.posts.map((p) => p.versionId).join(',');
  const read = () => api.get<{ posts: { versionId: string; state: ShownState; handoff?: 'manual' | 'automatic' | null }[] }>(`/api/brands/${brandId}/chat/shown?versions=${ids}`)
    .then((r) => {
      const now = Object.fromEntries(r.posts.map((x) => [x.versionId, x.state])) as Record<string, ShownState>;
      setShown(now);
      setHandoff(Object.fromEntries(r.posts.map((x) => [x.versionId, x.handoff ?? null])));
      return now;
    });
  useEffect(() => { if (ids) read().catch((e) => setErr(errorMessage(e))); }, [brandId, ids]);
  if (data.posts.length === 0) return <Card>{t('chat.week.none')}</Card>;
  const total = data.posts.length;
  const titles = (state: ShownState) => data.posts.filter((p) => shown?.[p.versionId] === state).map((p) => p.title);
  const waiting = data.posts.filter((p) => shown?.[p.versionId] === 'waiting');
  const approved = titles('approved').length;
  // How the approved ones leave: what the owner has to do next, never a promise that a post waiting for their hand "will go out".
  const leaving = (how: 'manual' | 'automatic' | null) => data.posts.filter((p) => shown?.[p.versionId] === 'approved' && handoff[p.versionId] === how).length;
  const stillFailed = waiting.filter((p) => failed.includes(p.versionId)).map((p) => p.title);
  const approve = async () => {
    setBusy(true); setErr('');
    const sent = waiting.map((p) => p.versionId);
    let lost: unknown = null;
    try {
      const r = await api.post<{ approved: number; results: { versionId: string; ok: boolean }[] }>(`/api/brands/${brandId}/chat/approve`, { versionIds: sent });
      setFailed(r.results.filter((x) => !x.ok).map((x) => x.versionId));
    } catch (e) { lost = e; }
    // What happened is what the server holds now: if the answer was lost but nothing tapped still waits, the lines above say it all.
    const now = await read().catch((e) => { setErr(errorMessage(e)); return null; });
    if (lost && (!now || sent.some((id) => now[id] === 'waiting'))) setErr(errorMessage(lost));
    setBusy(false);
  };
  const decided = shown && (waiting.length < total || stillFailed.length > 0);
  return (
    <Card title={!shown || waiting.length ? t('chat.week.title', { count: shown ? waiting.length : total }) : t('chat.week.shown', { count: total })}>
      <div className="ch-posts">
        {data.posts.map((p) => (
          <div key={p.versionId} className="ch-post">
            <img src={`/api/versions/${p.versionId}/thumb?w=480`} alt="" loading="lazy" />
            <div className="ch-post-body">
              <div className="ch-post-when">{p.slotAt ? fmtDateTime(p.slotAt, data.timezone) : ''}{p.network ? ` · ${p.network}` : ''}</div>
              <div className="ch-post-text">{p.text}</div>
            </div>
          </div>
        ))}
      </div>
      {decided && (
        <div className="ch-done">
          {approved > 0 && <div>{approved === total ? t('chat.week.approved', { count: total }) : t('chat.week.approvedSome', { count: approved, total })}</div>}
          {leaving('manual') > 0 && <div className="ch-small">{t('chat.week.byHand', { count: leaving('manual') })}</div>}
          {leaving('automatic') > 0 && <div className="ch-small">{t('chat.week.automatic', { count: leaving('automatic') })}</div>}
          {leaving(null) > 0 && <div className="ch-small">{t('chat.week.unscheduled', { count: leaving(null) })}</div>}
          {titles('replaced').length > 0 && <div className="ch-err">{t('chat.week.replaced', { titles: titles('replaced').join(', ') })}</div>}
          {titles('changes_requested').length > 0 && <div className="ch-small">{t('chat.week.changesAsked', { titles: titles('changes_requested').join(', ') })}</div>}
          {titles('gone').length > 0 && <div className="ch-small">{t('chat.week.gone', { titles: titles('gone').join(', ') })}</div>}
          {stillFailed.length > 0 && <div className="ch-err">{t('chat.week.someFailed', { titles: stillFailed.join(', ') })}</div>}
        </div>
      )}
      {waiting.length > 0 && (
        <>
          {data.checklist.length > 0 && <p className="ch-small">{t('chat.week.confirm', { items: data.checklist.join('; ') })}</p>}
          <button className="ch-btn ch-btn-primary ch-wide" disabled={busy} onClick={approve}>
            {busy ? t('chat.week.approving') : waiting.length === total ? t('chat.week.approveAll') : t('chat.week.approveWaiting', { count: waiting.length })}
          </button>
        </>
      )}
      {err && <div className="ch-err">{err}</div>}
    </Card>
  );
}

/** A month's plan. Like the week card, it reads what became of this plan revision and offers Approve only while it still waits. */
function PlanCard({ data, brandId }: { data: PlanCardData | null; brandId: string }) {
  const [now, setNow] = useState<'in_review' | 'approved' | 'changes_requested' | 'replaced' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const read = () => api.get<{ latest: { id: string } | null; revisions: { id: string; state: string }[] }>(`/api/brands/${brandId}/plans?month=${data!.month}`)
    .then((m) => {
      const state = m.revisions.find((r) => r.id === data!.planId)?.state;
      const now = state === 'approved' ? 'approved' : state === 'superseded' || m.latest?.id !== data!.planId ? 'replaced' : state === 'in_review' ? 'in_review' : 'changes_requested';
      setNow(now);
      return now;
    });
  useEffect(() => { if (data) read().catch((e) => setErr(errorMessage(e))); }, [brandId, data?.planId]);
  if (!data) return <Card>{t('chat.plan.none')}</Card>;
  const approve = async () => {
    setBusy(true); setErr('');
    let lost: unknown = null;
    try { await api.post(`/api/plans/${data.planId}/approve`); } catch (e) { lost = e; }
    // Approved after all (only the answer was lost): the card says so, without an error next to it.
    const now = await read().catch((e) => { setErr(errorMessage(e)); return null; });
    if (lost && now !== 'approved') setErr(errorMessage(lost));
    setBusy(false);
  };
  return (
    <Card title={t('chat.plan.title', { month: monthLabel(data.month) })}>
      <p className="ch-summary">{data.summary}</p>
      <ul className="ch-list">
        {data.items.map((i) => <li key={i.day + i.theme}><span className="ch-day">{DateTime.fromISO(i.day).toFormat('ccc d')}</span> {i.theme}</li>)}
      </ul>
      {data.questions.length > 0 && (
        <div className="ch-asks"><div className="ch-small">{t('chat.plan.asks')}</div>{data.questions.map((q) => <p key={q.id}>{q.question}</p>)}</div>
      )}
      {now === 'in_review' && <button className="ch-btn ch-btn-primary ch-wide" disabled={busy} onClick={approve}>{t('chat.plan.approve')}</button>}
      {now === 'approved' && <div className="ch-done">{t('chat.plan.approved')}</div>}
      {now === 'changes_requested' && <div className="ch-small">{t('chat.plan.changesAsked')}</div>}
      {now === 'replaced' && <div className="ch-small">{t('chat.plan.replaced')}</div>}
      {err && <div className="ch-err">{err}</div>}
    </Card>
  );
}

/** What a yes/no card answered when it was tapped: 'done', 'no' (the owner cancelled), or null (not answered, or refused for a reason). */
function answered(status: string, result: string | undefined): 'done' | 'no' | null {
  const r = status === 'complete' ? parse<{ done?: boolean; reason?: string }>(result) : null;
  return r?.done === true ? 'done' : r?.done === false && r.reason === CANCELLED ? 'no' : null;
}
const CANCELLED = 'the owner cancelled';
/**
 * The Idempotency-Key of a card's tap: its tool call id. If the tap reached the API but the answer was lost, tapping again (here, after a
 * reload or on another device) gets the first answer back: the change is done once and the card says it was done.
 */
const tapKey = (toolCallId: string, part?: string) => `chat:${toolCallId}${part ? `:${part}` : ''}`;
/** The answer a completed tool call carries (only a completed one has it). */
const resultOf = (props: object) => ('result' in props ? (props as { result?: string }).result : undefined);

/** A yes/no card for something the owner asked for: it happens only when they tap the button. */
function Confirm({ title, children, status, onYes, respond, doneText, yesText, result }: {
  title: string; children: ReactNode; status: string; onYes: () => Promise<unknown>; respond?: (r: unknown) => Promise<void>; doneText: string; yesText?: string; result?: string;
}) {
  const [tapped, setState] = useState<'idle' | 'busy' | 'done' | 'no'>('idle');
  // Back with the conversation, the card says what the tap did then (the answer it gave, kept with the conversation); it does nothing again.
  const state = tapped === 'idle' ? answered(status, result) ?? tapped : tapped;
  const [err, setErr] = useState('');
  const yes = async () => {
    setState('busy'); setErr('');
    try { await onYes(); setState('done'); await respond?.({ done: true }); } catch (e) { setErr(errorMessage(e)); setState('idle'); }
  };
  const no = async () => { setState('no'); await respond?.({ done: false, reason: CANCELLED }); };
  return (
    <Card title={title}>
      {children}
      {state === 'done' ? <div className="ch-done">{doneText}</div> : state === 'no' ? <div className="ch-small">{t('chat.change.cancelled')}</div> : status !== 'complete' && (
        <div className="ch-actions">
          <button className="ch-btn ch-btn-primary" disabled={state === 'busy' || status !== 'executing'} onClick={yes}>{yesText ?? t('chat.change.send')}</button>
          <button className="ch-btn" disabled={state === 'busy' || status !== 'executing'} onClick={no}>{t('chat.change.cancel')}</button>
        </div>
      )}
      {err && <div className="ch-err">{err}</div>}
    </Card>
  );
}

function FactsCard({ data }: { data: FactsCardData | null }) {
  if (!data || (!data.url && data.facts.length === 0)) return <Card>{t('chat.facts.none')}</Card>;
  return (
    <Card title={t('chat.facts.title')}>
      {data.url && <p className="ch-small">{t('chat.facts.website', { url: site(data.url) })}</p>}
      <ul className="ch-list">{data.facts.map((f) => <li key={f.id}>{f.text}</li>)}</ul>
    </Card>
  );
}

/**
 * The owner changes one business fact: the card shows exactly what is saved (and what it replaces), and saving it is their approval.
 * It is made against the approved facts the card read, so a change made elsewhere meanwhile is refused rather than overwritten.
 * A business whose facts come from its website changes them there; the card says so and saves nothing.
 */
function FactChange({ brandId, op, factId, text, status, respond, result, tapId }: {
  brandId: string; op: 'add' | 'update' | 'remove'; factId?: string; text?: string; status: string; respond?: (r: unknown) => Promise<void>; result?: string; tapId: string;
}) {
  const [facts, setFacts] = useState<BrandFacts | null | undefined>(undefined);
  const told = useRef(false);
  useEffect(() => { api.get<BrandFacts>(`/api/brands/${brandId}/facts`).then(setFacts).catch(() => setFacts(null)); }, [brandId]);
  const before = op === 'add' ? null : facts?.approved?.content.facts.find((f) => f.id === factId) ?? null;
  const refusal = facts === undefined ? null : facts?.published ? `facts come from the website ${facts.published.url}; nothing saved`
    : !facts?.approved ? 'there are no approved facts yet; nothing saved' : op !== 'add' && !before ? `no fact with id ${factId}; nothing saved` : null;
  useEffect(() => {
    if (refusal && status === 'executing' && !told.current) { told.current = true; void respond?.({ done: false, reason: refusal }); }
  }, [refusal, status, respond]);
  const title = t(op === 'add' ? 'chat.fact.add' : op === 'update' ? 'chat.fact.update' : 'chat.fact.remove');
  // Back with the conversation: what the tap did then, not a new lookup (a removed fact is gone; a saved one is no longer "new").
  const then = answered(status, result);
  if (then) return <Card title={title}>{text && <p><strong>{text}</strong></p>}{then === 'done' ? <div className="ch-done">{t('chat.fact.saved')}</div> : <div className="ch-small">{t('chat.change.cancelled')}</div>}</Card>;
  if (facts === undefined) return <Card>{t('chat.loading')}</Card>;
  if (facts?.published) return <Card title={title}><p>{t('chat.facts.website', { url: site(facts.published.url) })}</p></Card>;
  if (!facts?.approved) return <Card title={title}><p>{t('chat.facts.none')}</p></Card>;
  if (op !== 'add' && !before) return <Card title={title}><p>{t('chat.fact.unknown')}</p></Card>;
  const basedOn = facts.approved.id;
  return (
    <Confirm title={title} status={status} respond={respond} doneText={t('chat.fact.saved')} yesText={t('chat.fact.save')}
      onYes={() => api.post(`/api/brands/${brandId}/facts/owner-change`, op === 'add' ? { op, basedOn, text } : op === 'update' ? { op, basedOn, factId, text } : { op, basedOn, factId }, tapKey(tapId))}>
      {op === 'remove' ? <p><s>{before!.text}</s></p> : <p><strong>{text}</strong></p>}
      {op === 'update' && <p className="ch-small">{t('chat.fact.was', { text: before!.text })}</p>}
    </Confirm>
  );
}

function Tools() {
  const { brand } = useSession();
  const today = DateTime.now().setZone(brand.timezone);
  useAgentContext({ description: 'The business and today', value: { business: brand.name, timezone: brand.timezone, today: today.toISODate() } });
  useConfigureSuggestions({
    suggestions: [
      { title: t('chat.suggest.week'), message: t('chat.suggest.week') },
      { title: t('chat.suggest.plan'), message: t('chat.suggest.plan') },
      { title: t('chat.suggest.results'), message: t('chat.suggest.results') },
    ],
    available: 'always',
  });

  useFrontendTool({
    name: 'show_waiting_posts',
    description: 'Shows the owner the posts waiting for their approval, with one Approve all button. Returns each post (versionId, title, date, text).',
    parameters: z.object({}),
    handler: async () => ({ ...(await api.get<Waiting>(`/api/brands/${brand.id}/chat/waiting`)), [SHOWN]: true }),
    render: ({ status, result }) => {
      const data = parse<Waiting>(result);
      return status !== 'complete' || !data ? <Card>{t('chat.loading')}</Card> : <WeekCard data={data} brandId={brand.id} />;
    },
  }, [brand.id]);

  useHumanInTheLoop({
    name: 'request_change',
    description: 'Asks the drafting agent to change one waiting post. The owner confirms on the card. versionId comes from show_waiting_posts.',
    parameters: z.object({ versionId: z.string(), title: z.string(), note: z.string().describe('What to change, in the owner\'s words') }),
    render: (props) => { const { args, status, respond, toolCallId } = props; return (
      <Confirm result={resultOf(props)} title={t('chat.change.title')} status={status} respond={respond} doneText={t('chat.change.sent')}
        onYes={() => api.post(`/api/versions/${args.versionId}/request-changes`, { note: args.note }, tapKey(toolCallId))}>
        <p className="ch-small">{args.title}</p>
        <p>{args.note}</p>
      </Confirm>
    ); },
  }, []);

  useFrontendTool({
    name: 'show_plan',
    description: 'Shows the owner the newest monthly content plan (this month or later), with an Approve button when it waits for them. Returns the plan.',
    parameters: z.object({}),
    handler: async () => {
      const list = await api.get<{ id: string; month: string; state: string }[]>(`/api/brands/${brand.id}/plans`);
      const month = list.filter((p) => p.month >= today.toFormat('yyyy-MM') && p.state !== 'superseded').map((p) => p.month).sort().at(-1);
      if (!month) return null;
      const m = await api.get<{ latest: any }>(`/api/brands/${brand.id}/plans?month=${month}`);
      const l = m.latest;
      return l ? { [SHOWN]: true, month, planId: l.id, state: l.state, summary: l.summary, questions: l.questions ?? [],
        items: (l.items ?? []).map((i: any) => ({ day: i.day, theme: i.theme, content_type: i.content_type })) } : null;
    },
    render: ({ status, result }) => (status !== 'complete' ? <Card>{t('chat.loading')}</Card> : <PlanCard data={parse<PlanCardData>(result)} brandId={brand.id} />),
  }, [brand.id]);

  useHumanInTheLoop({
    name: 'request_plan_change',
    description: 'Asks the agent to revise a monthly plan, with the owner\'s comment. The owner confirms on the card. planId comes from show_plan.',
    parameters: z.object({ planId: z.string(), note: z.string() }),
    render: (props) => { const { args, status, respond, toolCallId } = props; return (
      <Confirm result={resultOf(props)} title={t('chat.planChange.title')} status={status} respond={respond} doneText={t('chat.change.sent')}
        onYes={async () => {
          await api.post(`/api/plans/${args.planId}/comments`, { body: args.note }, tapKey(toolCallId, 'comment'));
          await api.post(`/api/plans/${args.planId}/request-changes`, {}, tapKey(toolCallId, 'request'));
        }}>
        <p>{args.note}</p>
      </Confirm>
    ); },
  }, []);

  useHumanInTheLoop({
    name: 'add_promotion',
    description: 'Saves a promotion, event or focus the owner wants pushed, for the agent\'s next plans. Dates as YYYY-MM-DD. The owner confirms on the card.',
    parameters: z.object({ title: z.string(), details: z.string(), starts_on: z.string(), ends_on: z.string() }),
    render: (props) => { const { args, status, respond, toolCallId } = props; return (
      <Confirm result={resultOf(props)} title={t('chat.promo.title')} status={status} respond={respond} doneText={t('chat.promo.saved')} yesText={t('chat.promo.save')}
        onYes={() => api.post(`/api/brands/${brand.id}/promotions`, { title: args.title, details: args.details, starts_on: args.starts_on, ends_on: args.ends_on }, tapKey(toolCallId))}>
        <p><strong>{args.title}</strong></p>
        {args.details && <p>{args.details}</p>}
        <p className="ch-small">{args.starts_on} – {args.ends_on}</p>
      </Confirm>
    ); },
  }, [brand.id]);

  useFrontendTool({
    name: 'show_facts',
    description: 'Shows the owner the business facts the agent writes from (each with its id). Returns them, and the website they come from if any.',
    parameters: z.object({}),
    handler: async () => {
      const f = await api.get<BrandFacts>(`/api/brands/${brand.id}/facts`);
      const list = f.published ? f.published.facts : f.approved?.content.facts ?? [];
      return { [SHOWN]: true, url: f.published?.url ?? null, facts: list.map((x) => ({ id: x.id, text: x.text })) };
    },
    render: ({ status, result }) => (status !== 'complete' ? <Card>{t('chat.loading')}</Card> : <FactsCard data={parse<FactsCardData>(result)} />),
  }, [brand.id]);

  useHumanInTheLoop({
    name: 'change_fact',
    description: 'Saves a change the owner tells you about their business (hours, closures, prices, menu, services) to the facts the agent writes '
      + 'from. op add: text is the new fact as one plain sentence in the owner\'s own words. op update: factId (from show_facts) and the new text. '
      + 'op remove: factId. The owner confirms the exact text on the card; saving it is their approval.',
    parameters: z.object({ op: z.enum(['add', 'update', 'remove']), factId: z.string().optional(), text: z.string().optional() }),
    render: (props) => { const { args, status, respond, toolCallId } = props; return <FactChange brandId={brand.id} op={args.op ?? 'add'} factId={args.factId} text={args.text} status={status} respond={respond} result={resultOf(props)} tapId={toolCallId} />; },
  }, [brand.id]);

  useFrontendTool({
    name: 'show_results',
    description: 'Shows how the posts of the last 30 days did (numbers per network and the best post). Returns the summary.',
    parameters: z.object({}),
    handler: async () => {
      const m = await api.get<BrandMetrics>(`/api/brands/${brand.id}/metrics?from=${today.minus({ days: 30 }).toISODate()}`);
      const read = m.rows.filter((r) => r.latest);
      const score = (r: (typeof read)[number]) => (r.latest!.metrics.views ?? 0) + 10 * (r.latest!.metrics.likes ?? 0);
      const best = [...read].sort((a, b) => score(b) - score(a))[0];
      return { [SHOWN]: true, published: m.rows.length, networks: m.networks.map((n) => ({ network: n.network, posts: n.posts, totals: n.totals })), best: best ? best.publication.piece : null };
    },
    render: ({ status, result }) => {
      const d = parse<{ published: number; networks: { network: string; totals: Record<string, number> }[]; best: string | null }>(result);
      if (status !== 'complete' || !d) return <Card>{t('chat.loading')}</Card>;
      if (!d.published) return <Card>{t('chat.results.none')}</Card>;
      return (
        <Card title={t('chat.results.title')}>
          <p>{t('chat.results.posts', { count: d.published })}</p>
          {d.networks.map((n) => <p key={n.network} className="ch-small">{n.network}: {Object.entries(n.totals).map(([k, v]) => `${v} ${k}`).join(', ')}</p>)}
          {d.best && <p>{t('chat.results.best', { title: d.best })}</p>}
        </Card>
      );
    },
  }, [brand.id]);
  return null;
}

/** Sends a message as the owner and runs the agent: a suggestion, the greeting's buttons, a link from a notification. */
function useSend() {
  const { agent } = useAgent();
  const { copilotkit } = useCopilotKit();
  return (text: string) => {
    if (!agent) return;
    agent.addMessage({ id: crypto.randomUUID(), role: 'user', content: text });
    void copilotkit.runAgent({ agent });
  };
}

/** A link from a notification (/chat?ask=week) starts the conversation with that question. */
function Ask() {
  const [params, setParams] = useSearchParams();
  const { agent } = useAgent();
  const send = useSend();
  const sent = useRef(false);
  useEffect(() => {
    const ask = params.get('ask');
    const text = ask === 'week' ? t('chat.suggest.week') : ask === 'plan' ? t('chat.suggest.plan') : ask === 'results' ? t('chat.suggest.results') : null;
    if (!text || sent.current || !agent) return;
    sent.current = true;
    setParams({}, { replace: true });
    send(text);
  }, [params, agent, send, setParams]);
  return null;
}

/** The greeting on an empty chat: what is waiting for the owner right now, each with one tap to see it. */
function Welcome() {
  const { brand, me } = useSession();
  const { agent } = useAgent();
  const send = useSend();
  const [empty, setEmpty] = useState(true);
  const [waiting, setWaiting] = useState<number | null>(null);
  const [planMonth, setPlanMonth] = useState<string | null>(null);
  useEffect(() => {
    if (!agent) return;
    setEmpty(agent.messages.length === 0);
    const sub = agent.subscribe({ onMessagesChanged: ({ messages }) => setEmpty(messages.length === 0) });
    return () => sub.unsubscribe();
  }, [agent]);
  useEffect(() => {
    api.get<Waiting>(`/api/brands/${brand.id}/chat/waiting`).then((w) => setWaiting(w.posts.length)).catch(() => setWaiting(null));
    api.get<{ month: string; state: string }[]>(`/api/brands/${brand.id}/plans`)
      .then((l) => setPlanMonth(l.filter((x) => x.state === 'in_review').map((x) => x.month).sort()[0] ?? null)).catch(() => setPlanMonth(null));
  }, [brand.id]);
  if (!empty) return null;
  const first = me.user.name?.split(' ')[0];
  return (
    <div className="ch-welcome">
      <h1>{first ? t('chat.hiName', { name: first }) : t('chat.hi')}</h1>
      <p>{t('chat.welcome')}</p>
      {waiting !== null && waiting > 0 && (
        <div className="ch-todo">
          <strong>{t('chat.week.title', { count: waiting })}</strong>
          <button className="ch-btn ch-btn-primary" onClick={() => send(t('chat.suggest.week'))}>{t('chat.see')}</button>
        </div>
      )}
      {planMonth && (
        <div className="ch-todo">
          <strong>{t('chat.planWaiting', { month: monthLabel(planMonth) })}</strong>
          <button className="ch-btn ch-btn-primary" onClick={() => send(t('chat.suggest.plan'))}>{t('chat.see')}</button>
        </div>
      )}
      {waiting === 0 && !planMonth && <div className="ch-todo"><span>{t('chat.allDone')}</span></div>}
      <div className="ch-quick">
        {(['chat.suggest.week', 'chat.suggest.plan', 'chat.suggest.results'] as const).map((k) => <button key={k} className="ch-btn" onClick={() => send(t(k))}>{t(k)}</button>)}
      </div>
    </div>
  );
}

/**
 * What to tell the owner when a turn ends in an error. The server ends a turn it could not keep, or that a restart cut off, with its own
 * note (a RUN_ERROR the runtime marks INCOMPLETE_STREAM, services/chat-runner.ts): the answer may have been shown and still be lost, so
 * "I couldn't answer" would be untrue. Anything else is the model or the connection failing.
 */
function problemOf(error: unknown): 'chat.unavailable' | 'chat.notSaved' | 'chat.interrupted' {
  const e = error as { code?: string; message?: string } | undefined;
  if (e?.code !== 'INCOMPLETE_STREAM') return 'chat.unavailable';
  if (e.message?.startsWith('This answer could not be saved')) return 'chat.notSaved';
  if (e.message?.startsWith('The chat restarted')) return 'chat.interrupted';
  return 'chat.unavailable';
}

export function ChatPage() {
  const { brand } = useSession();

  // The server keeps each conversation for its person and brand only (chat_thread) and says which one to open: the one this browser
  // has when it is theirs here, else their latest in this brand (so another device opens it too), else a new one.
  const key = `chat.thread.${brand.id}`;
  const [problem, setProblem] = useState<'' | 'chat.unavailable' | 'chat.notSaved' | 'chat.interrupted'>('');
  const [threadId, setThreadId] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setThreadId(null);
    const local = localStorage.getItem(key);
    api.get<{ threadId: string; existing: boolean }>(`/api/brands/${brand.id}/chat/conversation${local ? `?current=${encodeURIComponent(local)}` : ''}`)
      .then((c) => { if (!live) return; localStorage.setItem(key, c.threadId); setThreadId(c.threadId); })
      .catch((e) => { if (live) { console.error('chat', e); setProblem('chat.unavailable'); } });
    return () => { live = false; };
  }, [key, brand.id]);
  return (
    <div className="ch-page dark">
      <header className="ch-head">
        <div className="ch-brand">{brand.name}</div>
        <nav className="ch-head-actions">

          <Link to="/" className="btn btn-small btn-ghost">{t('chat.advanced')}</Link>
        </nav>
      </header>

      {/* One provider per brand: the conversation, its cards and what the model is told never carry over from another brand. The server
          keeps the history per person and brand, and needs the brand said (x-brand-id). */}
      <CopilotKitProvider key={brand.id} runtimeUrl="/api/copilotkit" headers={{ 'x-requested-by': 'studio', 'x-brand-id': brand.id }} onError={(e) => { console.error('chat', e.code, e.error); setProblem(problemOf(e.error)); }}>
        <Tools />
        <Ask />
        {problem && <div className="ch-problem" role="alert">{t(problem)}</div>}
        <div className="ch-body">
          <Welcome />
          {threadId && <PhotoChat key={threadId} threadId={threadId} labels={{ welcomeMessageText: t('chat.welcome'), chatInputPlaceholder: t('chat.placeholder'), modalHeaderTitle: brand.name }} />}
        </div>
      </CopilotKitProvider>
    </div>
  );
}
